import BaseComponent from '/webresources-sources/js/components/base-component';
import { defineComponent } from '/webresources-sources/js/util/jquery';
import { hasCookieConsent } from '/webresources-sources/js/util/cookie-consent';
import MarkerClusterer from './map-utils/MarkerClusterer';
import loadScript from './map-utils/load-script';
import debounce from './map-utils/debounce';
import { inPolygons } from './map-utils/geometry';

import bayernPoints from './map-regions/bayern';
import oberbayernPoints from './map-regions/oberbayern';
import niederbayernPoints from './map-regions/niederbayern';
import oberpfalzPoints from './map-regions/oberpfalz';
import oberfrankenPoints from './map-regions/oberfranken';
import mittelfrankenPoints from './map-regions/mittelfranken';
import unterfrankenPoints from './map-regions/unterfranken';
import schwabenPoints from './map-regions/schwaben';

const REGIONS = {
    oberbayern: oberbayernPoints,
    ostbayern: [niederbayernPoints, oberpfalzPoints],
    franken: [oberfrankenPoints, mittelfrankenPoints, unterfrankenPoints],
    schwaben: schwabenPoints,
};

import mapStyles from './map-styles/default';
import createHTMLMapMarker from './map-utils/html-map-marker';

const removeTrailingSlash = (string) => {
    return string?.endsWith('/') ? string.slice(0, -1) : string;
};

export default class MapElement extends BaseComponent {
    constructor(options) {
        super(options);
        this._googlemap = null;
        this._markers = [];
        this._bounds = false;
    }

    properties() {
        return {
            locations: [],
            description: '',
            filter: {},
            limitBounds: true,
            mapsUrl: 'https://maps.googleapis.com/maps/api/js?libraries=places,geometry&language=de&callback=Function.prototype',
            mapsApiKey: '',
            cookieConsentServiceId: 'googleMaps',
            infoWindowReadMoreLabel: 'Read more1234',
            clusterButtonAriaLabel: 'Zoom in to reveal clustered locations',
            skipLinkLabel: 'Skip map',
        };
    }

    get googleMapsUrl() {
        return `${this.mapsUrl}&key=${this.mapsApiKey}`;
    }

    connected() {
        if (this.cookieConsentServiceId !== undefined && this.cookieConsentServiceId !== '') {
            hasCookieConsent(this.cookieConsentServiceId, () => {
                loadScript(this.googleMapsUrl)
                    .then(() => this.initialize())
                    .catch((error) => console.log('Error loading google maps script', error));
            });
        } else {
            // map-widget-page does not require cookie consent
            loadScript(this.googleMapsUrl)
                .then(() => this.initialize())
                .catch((error) => console.log('Error loading google maps script', error));
        }
    }

    watch() {
        return {
            locations: (locations) => {
                this.removeMarkers();
                this.addMarkers(locations, true);
            },
            filter: (filter) => {
                Object.keys(REGIONS).forEach((region) => {
                    const polygon = this._regionPolygons[region];
                    if (filter.regions.includes(region)) polygon.setMap(this._googlemap);
                    else polygon.setMap(null);
                });

                this.removeMarkers();
                this.addMarkers(this.locations, true, filter.regions, filter.types);
            },
        };
    }

    events() {
        return {
            '[zoom-in]': {
                click: () => {
                    this._googlemap.setZoom(this._googlemap.getZoom() + 1);
                },
            },
            '[zoom-out]': {
                click: () => {
                    this._googlemap.setZoom(this._googlemap.getZoom() - 1);
                },
            },
            '[on-location]': {
                click: () => {
                    this.requestLocation();
                },
            },
        };
    }

    initialize() {
        const center = new google.maps.LatLng(48.94143410620144, 11.259483197848152);

        let initialZoom = 6;
        if (window.matchMedia('(min-width: 992px)').matches) {
            initialZoom = 7;
        }

        this._googlemap = new google.maps.Map(this.element.querySelector('.google-map'), {
            zoom: initialZoom,
            minZoom: initialZoom,
            maxZoom: 15,
            center: center,
            disableDefaultUI: true,
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            streetViewControl: false,
            fullscreenControl: false,
            mapTypeControl: false,
            styles: mapStyles,
        });

        // Append the Polygon to map when the map is ready
        // https://developers.google.com/maps/documentation/javascript/examples/polygon-hole
        const allPoints = [
            new google.maps.LatLng(0, -90),
            new google.maps.LatLng(0, 90),
            new google.maps.LatLng(90, -90),
            new google.maps.LatLng(90, 90),
        ];

        const bayernRegion = new google.maps.Polygon({
            paths: [allPoints, bayernPoints],
            // paths: [allPoints, oberbayernPoints],
            strokeColor: '#008abb',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#008abb',
            fillOpacity: 0.6,
        });
        bayernRegion.setMap(this._googlemap);

        this._strictBounds = new google.maps.LatLngBounds();
        bayernPoints.forEach((point) => this._strictBounds.extend(new google.maps.LatLng(point.lat, point.lng)));

        this._regionPolygons = {};
        Object.keys(REGIONS).forEach((region) => {
            this._regionPolygons[region] = new google.maps.Polygon({
                paths: REGIONS[region],
                strokeColor: '#008abb',
                strokeOpacity: 0.8,
                strokeWeight: 0,
                fillColor: '#008abb',
                fillOpacity: 0.025,
            });
        });
        this._subRegionPolygons = {
            oberbayern: [
                new google.maps.Polygon({
                    paths: oberbayernPoints,
                }),
            ],
            ostbayern: [
                new google.maps.Polygon({
                    paths: niederbayernPoints,
                }),
                new google.maps.Polygon({
                    paths: oberpfalzPoints,
                }),
            ],
            franken: [
                new google.maps.Polygon({
                    paths: oberfrankenPoints,
                }),
                new google.maps.Polygon({
                    paths: mittelfrankenPoints,
                }),
                new google.maps.Polygon({
                    paths: unterfrankenPoints,
                }),
            ],
            schwaben: [
                new google.maps.Polygon({
                    paths: schwabenPoints,
                }),
            ],
        };

        // Create clusterer
        this.markerCluster = new MarkerClusterer(this._googlemap);
        this.markerCluster.setRenderClusterCallback((clusterSum) => {
            return `<button class="btn btn-icon-only btn-cluster" aria-label="${this.clusterButtonAriaLabel} - ${clusterSum}"><div class="h3 mb-0">${clusterSum}</div></button>`;
        });

        // go back when out of bayern bounds
        google.maps.event.addListener(this._googlemap, 'dragend', () => {
            if (this.limitBounds) {
                debounce(this.checkBounds(), 250);
            }
        });

        this.addMarkers(this.locations, false);

        // improve accessibility
        google.maps.event.addListener(this._googlemap, 'idle', () => {
            setTimeout(() => {
                // hide all canvas elements
                const canvases = this.element.querySelectorAll('canvas');
                for (const canvas of canvases) {
                    canvas.setAttribute('aria-hidden', 'true');
                }

                // remove all hidden markers from tabindex
                for (const marker of this._markers) {
                    const button = marker.div?.firstChild;
                    if (!button) continue;
                    const inBounds = this._googlemap.getBounds().contains(marker.getPosition());
                    button.setAttribute('tabindex', inBounds ? '0' : '-1');
                }
            }, 250);
        });

        // improve accessibility
        google.maps.event.addListener(this._googlemap, 'tilesloaded', () => {
            setTimeout(() => {
                const shortCutsButtonDuplicate = this.element.querySelector('.gm-style button[draggable="false"]');
                shortCutsButtonDuplicate.setAttribute('tabindex', '-1');
            }, 250);

            // make all google links and buttons not focusable via keyboard
            const linksAndButtons = this.element.querySelectorAll(
                '.gmnoprint a, .gmnoprint button, .gm-style-cc a, .gm-style-cc button, .gm-style a',
            );
            for (const linkOrButton of linksAndButtons) {
                if (linkOrButton.classList.contains('skip-link')) continue;
                linkOrButton.setAttribute('tabindex', '-1');
            }
        });

        // improve accessibility
        window.setTimeout(() => {
            const iframe = this.element.querySelector('iframe');
            if (iframe) {
                iframe.setAttribute('title', this.description);
            }
            const region = this.element.querySelector('[role="region"]');
            if (region) {
                region.setAttribute('aria-label', this.description);

                const skipLink = document.createElement('a');
                skipLink.href = '#after-map-jump-mark';
                skipLink.classList.add('skip-link', 'btn', 'btn-primary');
                skipLink.innerText = this.skipLinkLabel;
                region.insertBefore(skipLink, region.firstChild);
            }
        }, 250);
    }

    /**
     * Checks whether google map is within defined bounds
     * Moves the map back into defined bounds if not
     */
    checkBounds() {
        if (this._strictBounds.contains(this._googlemap.getCenter())) return;

        // We're out of bounds - Move the map back within the bounds

        let center = this._googlemap.getCenter(),
            x = center.lng(),
            y = center.lat(),
            maxX = this._strictBounds.getNorthEast().lng(),
            maxY = this._strictBounds.getNorthEast().lat(),
            minX = this._strictBounds.getSouthWest().lng(),
            minY = this._strictBounds.getSouthWest().lat();

        if (x < minX) x = minX;
        if (x > maxX) x = maxX;
        if (y < minY) y = minY;
        if (y > maxY) y = maxY;

        this._googlemap.setCenter(new google.maps.LatLng(y, x));
    }

    removeMarkers() {
        for (const mapMarker of this._markers) {
            mapMarker.setMap(null);
        }
        this._markers = [];
    }

    addMarkers(
        locations,
        fitBounds = false,
        regions = ['oberbayern', 'ostbayern', 'franken', 'schwaben'],
        types = ['default', 'hotel', 'story', 'rooster'],
    ) {
        if (!this._googlemap) {
            return;
        }

        const regionPolygons = [];
        regions.forEach((region) => {
            regionPolygons.push(...this._subRegionPolygons[region]);
        });

        this._bounds = new google.maps.LatLngBounds();

        const infoWindow = new google.maps.InfoWindow({
            content: ``,
            pixelOffset: new google.maps.Size(180, 86 / 2),
        });

        this._markers = [];

        let currentLatLng = undefined;

        locations.forEach((location) => {
            const latLng = new window.google.maps.LatLng(location.latitude, location.longitude);

            if (!inPolygons(latLng, regionPolygons)) return;
            if (!types.includes(location.pin)) return;

            this._bounds.extend(latLng);

            const currentUrlWithoutQuery = [
                window.location.protocol,
                '//',
                window.location.host,
                window.location.pathname,
            ].join('');
            const isCurrentLocation =
                removeTrailingSlash(location.link) === removeTrailingSlash(currentUrlWithoutQuery);

            if (isCurrentLocation) currentLatLng = latLng;

            const mapMarker = createHTMLMapMarker({
                map: this._googlemap,
                position: latLng,
                id: location.id,
                html: `<button class="btn btn-icon btn-marker marker-${location.pin} ${
                    isCurrentLocation ? 'marker-current' : ''
                }" role="button" aria-label="${location.title}"></button>`,
            });

            google.maps.event.addListener(mapMarker, 'click', () => {
                let ctaLabel = this.infoWindowReadMoreLabel;
                if (location.ctaLabel) {
                    ctaLabel = location.ctaLabel;
                }

                infoWindow.setContent(`
                    <div class="info-window">
                        ${
                            location.image
                                ? `<img class="image" src="${location.image}" width="120" alt="${location.title}">`
                                : ''
                        }
                        <div class="content">
                            <p class="h5">${this.htmlDecode(location.title)}</p>
                            ${
                                location.link
                                    ? `<a href="${location.link}" target="${
                                          location.isExternalLink ? '_blank' : '_self'
                                      }" class="link link-gradient">${ctaLabel}</a>`
                                    : ''
                            }
                        </div>
                    </div>
                `);
                infoWindow.open({
                    anchor: mapMarker,
                    map: this._googlemap,
                    shouldFocus: false,
                });
            });

            this._markers.push(mapMarker);
            //return mapMarker;
        });

        this.markerCluster.clearMarkers();
        this.markerCluster.addMarkers(this._markers);
        if (fitBounds) this.fitBounds();

        if (currentLatLng) {
            this._googlemap.setCenter(currentLatLng);
            this._googlemap.setZoom(15);
        }
    }

    htmlDecode(input) {
        const doc = new DOMParser().parseFromString(input, "text/html");
        return doc.documentElement.textContent;
    }

    fitBounds() {
        if (!this._googlemap || !this._bounds) return;
        this._googlemap.fitBounds(this._bounds);
    }

    requestLocation() {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    var latlng = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);
                    this.onLocation(latlng);
                },
                (error) => {
                    //raysono address for testing purposes
                    const latlng = new google.maps.LatLng(48.1257, 11.55719);
                    this.onLocation(latlng);
                    alert(
                        'Wir können deinen Standort derzeit nicht ermitteln. Bitte überprüfe deine Systemeinstellungen.',
                    );
                },
            );
        }
    }

    onLocation(latLng) {
        // TODO: what to do if someone is way out of the region ?! The map will always re-center back to bayern...
        /*new google.maps.Marker({
            position: latLng,
            map: this._googlemap,
            icon: this._icons['location'],
        });*/

        createHTMLMapMarker({
            map: this._googlemap,
            position: latLng,
            id: location.id,
            html: `<button class="btn btn-icon btn-marker marker-location" role="button" aria-label="Your location"></button>`,
        });

        this._googlemap.setCenter(latLng);
        this._googlemap.setZoom(12);
    }
}

defineComponent('map-element', MapElement);
